import React, { useEffect, useRef } from 'react';
import { isMobile } from '@/biz/tool';
import toast from 'react-hot-toast';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import { Input } from 'aha-components';
import styles from './ModalAddress.module.less';
import AddressProgress from './AddressProgress';

const SelectProductContent: React.FC<{
  autoFocus: boolean;
  setShowAddressModal: (show: boolean) => void;
  skuListData: any[];
  setSkuListData: (skuListData: any[]) => void;
  onContinue: () => void;
}> = ({
  autoFocus,
  setShowAddressModal,
  skuListData = [],
  setSkuListData,
  onContinue
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { product, order } = detailData;
  const { setShowModalShowImage } = useWebModalStore();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoFocus) return;

    const timer = window.setTimeout(() => {
      const inputs = Array.from(
        containerRef.current?.querySelectorAll(
          'input:not([type="hidden"]):not([disabled])'
        ) || []
      ).filter(
        (element): element is HTMLInputElement =>
          element instanceof HTMLInputElement && element.offsetParent !== null
      );
      const firstEmptyInput = inputs.find(
        (element) => !String(element.value || '').trim()
      );
      (firstEmptyInput || inputs[0])?.focus();
    }, 50);

    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  const handleContinue = async () => {
    const res = await axiosInstance.post(`/order/${order?.id}/productInfo`, {
      skuList: skuListData
    });
    if (res.data.code === 0) {
      onContinue();
    } else {
      toast.error(res.data.message);
    }
  };

  const mobileStickyFooterClassName =
    'bg-white w-full fixed bottom-0 left-0 right-0 z-[1301] flex flex-col-reverse gap-2 border-t border-solid border-[#e8e8ec] px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]';

  return (
    <div
      ref={containerRef}
      className={isMobile() ? styles.mobileContent : styles.content}
    >
      <div
        className={`${styles.scrollBody} ${
          isMobile() ? 'pb-[calc(88px+env(safe-area-inset-bottom,0px))]' : ''
        }`}
      >
        <AddressProgress step={2} hasProductSelection />
        <div className="text-base font-semibold leading-6 mb-3">
          {t('modal.address.select_product_title', {
            defaultValue: 'Select your product'
          })}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-[#525252] text-sm font-normal leading-5">
            {t('modal.address.select_product_description', {
              defaultValue:
                'Please select the product options you want to receive. Your selection will be used to ship the product.'
            })}
          </span>
          <div className="self-stretch p-2 bg-[#FFF6ED] rounded-lg border border-[#FB6011] inline-flex justify-start items-center gap-2">
            <img
              className="w-11 h-11 object-contain rounded-lg border border-zinc-100 bg-[#fff]"
              src={product?.physicalProductPhoto}
            />
            <div className="flex-1 justify-start text-font&icon-primary text-sm font-normal leading-5">
              {' '}
              {product?.physicalProductName}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 mt-4">
          {skuListData?.map((item: any, index: number) => (
            <div key={index} className="flex flex-col gap-2">
              <div className="break-all">
                {item.label}
                <span className="text-red-500"> *</span>
              </div>
              <div className="text-[#A6A09B] text-sm font-normal leading-5">
                {item.description}
              </div>
              <Input
                placeholder={t('modal.address.enter_option', {
                  defaultValue: 'Enter your option'
                })}
                value={item?.value}
                onChange={(e) =>
                  setSkuListData(
                    skuListData?.map((item: any, idx: number) =>
                      idx === index ? { ...item, value: e.target.value } : item
                    )
                  )
                }
              />
              <div className="flex gap-2">
                {item.images &&
                  item.images.map((image: any, index: number) => (
                    <img
                      key={index}
                      src={image.src}
                      className="w-11 h-11 rounded-lg cursor-pointer"
                      onClick={() => setShowModalShowImage(true, image.src)}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className={`${
          isMobile()
            ? mobileStickyFooterClassName
            : `${styles.actionFooter} flex justify-end items-center gap-2`
        }`}
      >
        <div
          className={`${isMobile() ? 'w-full text-center' : ''} px-4 py-2 bg-white rounded-lg border border-zinc-200 text-sm font-medium leading-5 cursor-pointer ${isMobile() ? 'w-full' : ''}`}
          onClick={() => setShowAddressModal(false)}
        >
          {t('modal.address.cancel', { defaultValue: 'Cancel' })}
        </div>
        <div
          className={`bg-theme-color text-[#fff] px-4 py-2 rounded-lg text-sm font-medium leading-5 ${skuListData?.some((item: any) => !item.value) ? 'opacity-50' : 'cursor-pointer'} ${isMobile() ? 'w-full text-center' : ''}`}
          onClick={() => handleContinue()}
        >
          {t('modal.address.continue', { defaultValue: 'Continue' })}
        </div>
      </div>
    </div>
  );
};

export default SelectProductContent;
